/**
 * tm/pages/CustomerCollect.tsx — 고객 진행 안내 안의 「자료 보내기」 (2026-10-13 지도사 「필요서류 제출 화면이 여기서도 열리지? 가장 사용이 편해야 해」
 *  · 「기존 자료수집 페이지는 바꾸지 말고 고객 사이트에서 열 때만」)
 *
 *  인지공학 설계(지도사 「인지공학 교수와 함께 설계」 — 이번 판은 별도 검수자 없이 그 기준으로 직접 설계):
 *   ① 같은 탭 · 같은 모양 — 진행 안내와 글꼴 · 색 · 버튼이 같아 「다른 사이트로 넘어갔다」는 느낌이 없다(맥락 전환 비용) · 휴대폰 뒤로 = 진행 안내
 *   ② 한 줄로 쭉 — 묶음 탭(개인 · 법인) 대신 한 목록 · 할 일 순서(다시 보내 주세요 → 올려 주세요(꼭 필요한 것 먼저) → 확인 중 → 확인 끝(접힘)) — 기억해야 할 것을 줄인다
 *   ③ 서류 하나 = 큰 버튼 하나(52px) — 누르면 휴대폰이 「사진 찍기 · 앨범 · 파일」을 알아서 보여 준다 · 여러 장 한 번에
 *   ④ 상태 낱말은 네 개만, 진행 안내와 같은 말 — 올려 주세요 · 다시 보내 주세요 · 임지도사가 확인하고 있어요 · 확인 끝났어요
 *   ⑤ 맨 위 한 줄 진행 — 「5개 중 2개 보냈어요」 + 막대 · 다 보내면 「모두 보냈어요」
 *   ⑥ 실수 되돌리기 — 올린 뒤 30분 안에는 「잘못 올렸으면 지우기(n분 남음)」 · 실패는 그 서류 아래 빨간 줄 + 다시 누르기
 *  예전 자료수집 화면(/portal/co · PortalScreen.jsx)은 그대로 — 같은 공개 API(/api/portal/…)만 쓴다
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { portalSizeError, portalHttpError } from '../../portal/uploadLimit';
import { AppBar, Button, Divider, FONT, G, Group, Icon, JIDO, ListHeader, MoreText, NAVY, FILL, ProgressBar, RED, TEAL, GREEN, TextLink, Top, clamp } from '../portal-ui/tds';

interface CGuide { id: string; type: string; url?: string | null; label?: string | null; fileName?: string | null }
interface CItem { id: string; label: string; description?: string | null; required?: boolean; sensitive?: boolean; type: string; section?: string | null; projectId: string; options?: string[]; guides?: CGuide[] }
interface CUpload { id: string; itemId: string; projectId: string; type: string; fileName?: string | null; text?: string | null; status: string; rejectReason?: string | null; createdAt?: string | null }
interface CData { name: string; status?: string; items: CItem[]; uploads: CUpload[] }
type St = 'redo' | 'need' | 'check' | 'done';

const WORD: Record<St, string> = { redo: '다시 보내 주세요', need: '올려 주세요', check: `${JIDO}가 확인하고 있어요`, done: '확인 끝났어요' };
const ACCEPT = 'image/*,application/pdf,.pdf,.hwp,.hwpx,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.heic';
const DEL_MIN = 30;   // 서버 규칙 — 올린 뒤 30분 안에만 지울 수 있다

function stateOf(item: CItem, ups: CUpload[]): St {
  const mine = ups.filter(u => u.itemId === item.id);
  const app = mine.filter(u => u.status === 'approved').length, pend = mine.filter(u => u.status === 'pending').length, rej = mine.filter(u => u.status === 'rejected').length;
  if (app > 0 && pend === 0) return 'done';
  if (rej > 0 && pend === 0 && app === 0) return 'redo';
  if (pend > 0) return 'check';
  return 'need';
}
function minsLeft(at?: string | null): number {
  const t = at ? new Date(at).getTime() : NaN;
  return isFinite(t) ? Math.max(0, Math.ceil(DEL_MIN - (Date.now() - t) / 60000)) : 0;
}
async function readJson(r: Response): Promise<{ success?: boolean; error?: string; data?: unknown } & Record<string, unknown>> {
  try { return await r.json() as { success?: boolean; error?: string; data?: unknown } & Record<string, unknown>; } catch { return {}; }
}

/** wide — PC: 진행 안내와 같은 두 칸 틀(왼쪽 요약 · 업무별 · 돌아가기 / 오른쪽 자료 목록) · 화면 폭이 갑자기 좁아지지 않게(2026-10-27 지도사 「자료 보내기 누르니까 확 좁아들면서 어색」)
 *  aside — 왼쪽 맨 아래에 붙일 것(문의 단추) */
export default function CustomerCollect({ token, deadline, onClose, toast, wide = false, aside }: { token: string; deadline?: string | null; onClose: () => void; toast: (m: string) => void; wide?: boolean; aside?: ReactNode }) {
  const [data, setData] = useState<CData | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');                   // 보내는 중인 서류 id
  const [fail, setFail] = useState<Record<string, string>>({});
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [picks, setPicks] = useState<Record<string, string[]>>({});
  const [showDone, setShowDone] = useState(false);
  const [secSel, setSecSel] = useState('');   // PC 왼쪽 「업무별」에서 고른 묶음('' = 전체)
  const [, tick] = useState(0);
  const files = useRef<Record<string, HTMLInputElement | null>>({});

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/portal/co/${encodeURIComponent(token)}`);
      const j = await readJson(r);
      if (!r.ok || !Array.isArray((j as unknown as CData).items)) { setErr('자료 목록을 불러오지 못했어요. 잠시 뒤 다시 열어 주세요'); return; }
      setData(j as unknown as CData); setErr('');
    } catch { setErr('잠시 연결이 안 돼요. 조금 뒤 다시 열어 주세요'); }
  }, [token]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { const t = window.setInterval(() => tick(n => n + 1), 30000); return () => window.clearInterval(t); }, []);   // 「n분 남음」 줄이기

  const setFailOf = (id: string, m: string) => setFail(f => ({ ...f, [id]: m }));

  async function sendFiles(item: CItem, list: FileList | null) {
    if (!list || !list.length || busy) return;
    const tooBig = portalSizeError(list);   // 보내기 전에 미리(2026-11-02 · 파일 하나 300MB · 한 번에 1GB)
    if (tooBig) { setFailOf(item.id, tooBig); return; }
    setBusy(item.id); setFailOf(item.id, '');
    try {
      const fd = new FormData();
      fd.append('itemId', item.id); fd.append('uploader', '대표님');
      Array.from(list).forEach(f => fd.append('files', f));
      const r = await fetch(`/api/portal/${encodeURIComponent(item.projectId)}/upload`, { method: 'POST', body: fd });
      const j = await readJson(r);
      if (!r.ok || j.success === false) throw new Error(String(j.error || (r.status === 413 || r.status === 502 || r.status === 504 ? portalHttpError(r.status) : '')));
      toast(`「${item.label.length > 12 ? item.label.slice(0, 12) + '…' : item.label}」 보냈어요`);
      await load();
    } catch (e) {
      setFailOf(item.id, (e as Error).message || '보내지 못했어요. 인터넷 연결을 확인하고 다시 눌러 주세요');
    } finally { setBusy(''); }
  }
  async function sendText(item: CItem, text: string) {
    const v = text.trim(); if (!v || busy) return;
    setBusy(item.id); setFailOf(item.id, '');
    try {
      const r = await fetch(`/api/portal/${encodeURIComponent(item.projectId)}/text`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ itemId: item.id, uploader: '대표님', text: v }) });
      const j = await readJson(r);
      if (!r.ok || j.success === false) throw new Error(String(j.error || ''));
      setTexts(t => { const n = { ...t }; delete n[item.id]; return n; });
      setPicks(p => { const n = { ...p }; delete n[item.id]; return n; });
      toast('보냈어요');
      await load();
    } catch { setFailOf(item.id, '보내지 못했어요. 다시 눌러 주세요'); } finally { setBusy(''); }
  }
  async function remove(u: CUpload) {
    try {
      const r = await fetch(`/api/portal/${encodeURIComponent(u.projectId)}/upload/${encodeURIComponent(u.id)}`, { method: 'DELETE' });
      const j = await readJson(r);
      if (!r.ok || j.success === false) { toast(String(j.error || '지우지 못했어요')); return; }
      toast('지웠어요'); await load();
    } catch { toast('지우지 못했어요'); }
  }
  async function openGuide(item: CItem, g: CGuide) {
    if (g.type === 'link' && g.url) { window.open(/^https?:\/\//.test(g.url) ? g.url : 'https://' + g.url, '_blank', 'noopener'); return; }
    try {
      const r = await fetch(`/api/portal/${encodeURIComponent(item.projectId)}/guide/${encodeURIComponent(g.id)}/download`);
      const j = await readJson(r);
      const url = (j.data && typeof j.data === 'object' && 'url' in (j.data as Record<string, unknown>)) ? String((j.data as Record<string, unknown>).url) : String(j.url || '');
      if (url) window.open(url, '_blank', 'noopener'); else toast('안내 파일이 아직 없어요');
    } catch { toast('안내 파일을 열지 못했어요'); }
  }

  if (err) return <><AppBar onBack={onClose} /><Top title="자료 보내기" subBottom={err} /></>;
  if (!data) return <><AppBar onBack={onClose} /><div style={{ padding: '80px 32px', textAlign: 'center', color: G.g600, fontSize: 16 }}>불러오는 중이에요</div></>;
  if (data.status === 'closed') return <><AppBar onBack={onClose} /><Top title="자료 보내기" subBottom="자료 받기가 끝났어요. 궁금한 점은 아래 카카오톡 · 문자로 물어봐 주세요" /></>;

  const ups = data.uploads || [];
  const allRows = (data.items || []).map((it, i) => ({ it, st: stateOf(it, ups), i }));
  const secName = (s?: string | null) => String(s || '').replace(/^📌\s*/, '').replace(/^[^0-9A-Za-z가-힣([]+\s*/, '') || '자료';
  // 업무별 남은 수(PC 왼쪽) — 묶음 순서 그대로 · 공통이 맨 앞
  const secs: { key: string; name: string; left: number; total: number }[] = [];
  allRows.forEach(r => { const k = r.it.section || ''; let x = secs.find(y => y.key === k); if (!x) { x = { key: k, name: secName(k), left: 0, total: 0 }; secs.push(x); } x.total += 1; if (r.st === 'redo' || r.st === 'need') x.left += 1; });
  const pickSec = wide && secs.some(x => x.key === secSel) ? secSel : '';
  const rows = pickSec ? allRows.filter(r => (r.it.section || '') === pickSec) : allRows.slice();
  const rank: Record<St, number> = { redo: 0, need: 1, check: 2, done: 3 };
  rows.sort((a, b) => rank[a.st] - rank[b.st] || (a.st === 'need' ? (b.it.required ? 1 : 0) - (a.it.required ? 1 : 0) : 0) || a.i - b.i);
  const total = allRows.length, sent = allRows.filter(r => r.st === 'check' || r.st === 'done').length, left = total - sent;
  const multiSec = !pickSec && secs.length > 1;
  // 꽉 찬 버튼은 화면에 하나 — 맨 위 할 일(다시 보내기 · 올려 주세요 첫 줄)만 · 나머지는 옅은 버튼(4개 연속 주황 채움 · 2026-10-13 5개 분야 검수)
  // 보내기 단추는 모든 항목 같은 색(2026-10-28 지도사 「왜 색상이 달라」 — 첫 항목만 진하고 나머지는 연해 「안 눌리는 것」처럼 보였다)
  const dl = deadline ? (() => { const d = String(deadline).slice(0, 10).split('-').map(Number); return d.length === 3 && d[1] ? `${d[1]}월 ${d[2]}일(${'일월화수목금토'.charAt(new Date(d[0], d[1] - 1, d[2]).getDay())})까지` : ''; })() : '';
  const choice = (on: boolean): CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 52, padding: '0 16px', borderRadius: 14, border: `1.5px solid ${on ? FILL : G.g300}`, background: on ? FILL + '14' : '#fff', fontSize: 17, color: G.g900, cursor: 'pointer', fontFamily: FONT, textAlign: 'left' });

  const block = (it: CItem, st: St): ReactNode => {
    const mine = ups.filter(u => u.itemId === it.id);
    const reasons = Array.from(new Set(mine.filter(u => u.status === 'rejected').map(u => String(u.rejectReason || '').trim()).filter(Boolean)));
    const pend = mine.filter(u => u.status === 'pending');
    const canSend = st !== 'done';
    const isBusy = busy === it.id;
    const color = st === 'redo' ? RED : st === 'check' ? TEAL : st === 'done' ? GREEN : NAVY;
    const subBits = [multiSec ? secName(it.section) : '', it.required && st !== 'done' && st !== 'check' ? '꼭 필요해요' : ''].filter(Boolean);
    return (
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 600, color: G.g900, lineHeight: 1.4, wordBreak: 'keep-all', ...clamp(2) }}>{it.label}</div>
            {subBits.length > 0 && <div style={{ fontSize: 15, color: G.g600, marginTop: 2, ...clamp(1) }}>{subBits.join(' · ')}</div>}
          </div>
          {/* 상태 글자는 묶음 제목(다시 보내 주세요 · 올려 주세요 …)이 이미 말한다 — 같은 말 두 번 X · 확인 끝만 초록 표시 */}
          {st === 'done' && <span style={{ flexShrink: 0, fontSize: 15, fontWeight: 700, color, whiteSpace: 'nowrap', paddingTop: 2 }}>{WORD[st]}</span>}
        </div>
        {it.description && st !== 'done' && <div style={{ marginTop: 8 }}><MoreText lines={3} size={15} color={G.g700}>{it.description}</MoreText></div>}
        {st === 'redo' && reasons.map(r => (
          <div key={r} style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, background: '#FDECEC', color: '#B42318', fontSize: 15, lineHeight: 1.5, wordBreak: 'keep-all' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden style={{ flex: 'none', marginTop: 2 }}><circle cx="12" cy="12" r="11" fill="#C42626" /><path d="M12 6.5v7M12 17.2v.3" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" /></svg>
            <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{r}</span>
          </div>
        ))}
        {it.sensitive && canSend && <div style={{ marginTop: 8, fontSize: 15, color: G.g700 }}>보내 주신 내용은 {JIDO}만 볼 수 있어요</div>}
        {/* 보낸 것 — 확인 중인 것만 이름과 지우기(30분) */}
        {pend.map(u => {
          const m = minsLeft(u.createdAt);
          return (
            <div key={u.id} style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 12px', borderRadius: 10, background: G.g50 }}>
              <Icon name={u.type === 'file' ? 'doc' : 'memo'} size={18} color={G.g600} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 15, color: G.g800, ...clamp(1) }}>{it.sensitive ? '보낸 내용(가려 둠)' : u.type === 'file' ? (u.fileName || '보낸 파일') : (u.text || '보낸 내용')}</span>
              {m > 0 && <button type="button" className="tds-btn" onClick={() => void remove(u)} style={{ flexShrink: 0, minHeight: 44, padding: '0 4px', background: 'none', border: 'none', color: G.g700, fontSize: 15, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', fontFamily: FONT }}>지우기 · {m}분 남음</button>}
            </div>
          );
        })}
        {(it.guides || []).length > 0 && canSend && (
          <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', columnGap: 16 }}>
            {(it.guides || []).slice(0, 2).map(g => <TextLink key={g.id} color={G.g700} onClick={() => void openGuide(it, g)}>{(g.label || g.fileName || '어떻게 준비하나요').slice(0, 20)}</TextLink>)}
          </div>
        )}
        {/* 보내기 */}
        {canSend && it.type === 'file' && (
          <div style={{ marginTop: 12 }}>
            <input type="file" multiple accept={ACCEPT} ref={el => { files.current[it.id] = el; }} style={{ display: 'none' }}
              onChange={e => { void sendFiles(it, e.target.files); e.target.value = ''; }} />
            <Button kind="fill" onClick={() => { const el = files.current[it.id]; if (el && !isBusy) el.click(); }}>
              {isBusy ? '보내는 중…' : st === 'check' ? '하나 더 보내기' : st === 'redo' ? '다시 보내기' : '사진 · 파일 보내기'}
            </Button>
          </div>
        )}
        {canSend && (it.type === 'text' || it.type === 'text_long') && st !== 'check' && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {it.type === 'text_long'
              ? <textarea className="tds-in" rows={3} value={texts[it.id] || ''} onChange={e => setTexts(t => ({ ...t, [it.id]: e.target.value }))} placeholder="여기에 적어 주세요" style={{ width: '100%', padding: '12px 14px', borderRadius: 14, border: `1.5px solid ${G.g300}`, fontSize: 17, fontFamily: FONT, resize: 'vertical', outline: 'none' }} />
              : <input className="tds-in" value={texts[it.id] || ''} onChange={e => setTexts(t => ({ ...t, [it.id]: e.target.value }))} placeholder="여기에 적어 주세요" style={{ width: '100%', height: 52, padding: '0 14px', borderRadius: 14, border: `1.5px solid ${G.g300}`, fontSize: 17, fontFamily: FONT, outline: 'none' }} />}
            <Button kind="fill" muted={!(texts[it.id] || '').trim()} onClick={() => void sendText(it, texts[it.id] || '')}>{isBusy ? '보내는 중…' : '보내기'}</Button>
          </div>
        )}
        {canSend && (it.type === 'radio' || it.type === 'checkbox') && (it.options || []).length > 0 && st !== 'check' && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(it.options || []).map(o => {
              const on = (picks[it.id] || []).includes(o);
              return (
                <button key={o} type="button" className="tds-btn" aria-pressed={on} style={choice(on)}
                  onClick={() => setPicks(p => { const cur = p[it.id] || []; const next = it.type === 'radio' ? [o] : (cur.includes(o) ? cur.filter(x => x !== o) : cur.concat(o)); return { ...p, [it.id]: next }; })}>
                  <span style={{ width: 22, height: 22, flexShrink: 0, borderRadius: it.type === 'radio' ? '50%' : 6, boxShadow: `inset 0 0 0 ${on ? 7 : 2}px ${on ? FILL : G.g400}` }} />
                  <span style={{ minWidth: 0, ...clamp(2) }}>{o}</span>
                </button>
              );
            })}
            <Button kind="fill" muted={!(picks[it.id] || []).length} onClick={() => void sendText(it, (picks[it.id] || []).join(', '))}>{isBusy ? '보내는 중…' : '보내기'}</Button>
          </div>
        )}
        {fail[it.id] && <div role="alert" style={{ marginTop: 8, fontSize: 15, fontWeight: 600, color: '#B42318' }}>{fail[it.id]}</div>}
      </div>
    );
  };

  const groupOf = (st: St) => rows.filter(r => r.st === st);
  const sectionG = (st: St, title: string, m?: string) => {
    const L = groupOf(st); if (!L.length) return null;
    return (
      <Group m={m}>
        <ListHeader title={title} right={`${L.length}개`} />
        {L.map((r, i) => <div key={r.it.id}>{i > 0 && <Divider full />}{block(r.it, st)}</div>)}
      </Group>
    );
  };
  const doneL = groupOf('done');

  const head = (
    <Top title={left > 0 ? `보낼 자료 ${left}개` : '모두 보냈어요'} subTop={data.name} padT={wide ? 22 : 8} padB={wide ? 22 : 20} side={wide ? 20 : 32}
      subBottom={left > 0 ? `${total}개 중 ${sent}개 보냈어요${dl ? ` · ${dl}` : ''}` : `${JIDO}가 확인하면 진행 안내에서 바로 보여요`}
      extra={<div style={{ marginTop: 12 }}><ProgressBar pct={total ? Math.round(sent / total * 100) : 0} label={`자료 ${total}개 중 ${sent}개 보냄`} /></div>} />
  );
  const lists = (m?: string) => (
    <>
      {sectionG('redo', '다시 보내 주세요', m)}
      {sectionG('need', '올려 주세요', m)}
      {sectionG('check', `${JIDO}가 확인하고 있어요`, m)}
      {doneL.length > 0 && (
        <Group pad="4px 0" m={m}>
          <button type="button" className="tds-row" onClick={() => setShowDone(v => !v)} aria-expanded={showDone}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: FONT }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: G.g900 }}>확인 끝난 자료</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 15, fontWeight: 600, color: G.g700 }}>{doneL.length}개 · {showDone ? '접기' : '펼치기'}<Icon name={showDone ? 'u' : 'd'} size={16} sw={2} /></span>
          </button>
          {showDone && doneL.map((r, i) => <div key={r.it.id}>{i > 0 && <Divider full />}{block(r.it, 'done')}</div>)}
        </Group>
      )}
      {pickSec && !rows.some(r => r.st !== 'done') && <div style={{ padding: '24px 20px', fontSize: 16, color: G.g700 }}>이 업무 자료는 모두 보냈어요</div>}
    </>
  );
  /* 누가 보는지 미리 알려 드린다 — 팀장님 「받은 자료」는 회사 자료만(2026-10-24 지점장 페르소나 · 개인정보) */
  const privacy = (pad: string) => (
    <p style={{ margin: 0, padding: pad, fontSize: 15, lineHeight: 1.6, color: G.g700, wordBreak: 'keep-all' }}>
      재무제표 같은 회사 자료는 담당 팀장님도 상담 준비에 함께 볼 수 있어요. 신분증, 급여 자료 같은 개인 자료는 팀장님께 보이지 않아요.
    </p>
  );

  /* ── PC — 진행 안내와 같은 두 칸(왼쪽 400 · 오른쪽 목록) ── */
  if (wide) {
    return (
      <div style={{ maxWidth: 1168, margin: '0 auto', padding: '28px 24px', display: 'grid', gridTemplateColumns: '400px 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ minWidth: 0, position: 'sticky', top: 16 }}>
          <section style={{ background: '#fff', borderRadius: 20, overflow: 'hidden', marginBottom: 12 }}>
            <button type="button" className="tds-btn" onClick={onClose}
              style={{ display: 'flex', alignItems: 'center', gap: 4, margin: '14px 12px 0', minHeight: 40, padding: '0 8px', border: 'none', background: 'none', color: G.g700, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
              <Icon name="back" size={18} color={G.g700} sw={2} />진행 안내
            </button>
            {head}
          </section>
          {secs.length > 1 && (
            <Group m="0 0 12px">
              <ListHeader title="업무별" />
              {[{ key: '', name: '전체', left, total }].concat(secs).map(x => {
                const on = pickSec === x.key;
                return (
                  <button key={x.key || '_all'} type="button" className="tds-row" aria-pressed={on} onClick={() => setSecSel(x.key)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, minHeight: 52, padding: '0 20px', border: 'none', background: on ? G.g100 : 'transparent', cursor: 'pointer', fontFamily: FONT, textAlign: 'left' }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: on ? 700 : 500, color: G.g900, ...clamp(1) }}>{x.name}</span>
                    <span style={{ flexShrink: 0, fontSize: 15, fontWeight: 600, color: x.left ? NAVY : GREEN, fontVariantNumeric: 'tabular-nums' }}>{x.left ? `${x.left}개 남음` : '다 보냈어요'}</span>
                  </button>
                );
              })}
            </Group>
          )}
          {privacy('4px 20px 12px')}
          {aside}
        </div>
        <div style={{ minWidth: 0 }}>{lists('0 0 12px')}</div>
      </div>
    );
  }

  return (
    <>
      <AppBar onBack={onClose} />
      {head}
      <div style={{ height: 12 }} />
      {lists()}
      {privacy('8px 32px 12px')}
      <div style={{ padding: '4px 32px 32px' }}>
        <Button kind="gray" onClick={onClose}>진행 안내로 돌아가기</Button>
      </div>
    </>
  );
}
